import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import pinPrompt from "../utils/pinPrompt";
import { apiErrorMessage, detailToString } from '../utils/apiError';
import { emailSenderHeaders } from '../utils/emailSender';

const IMPACT_STYLE = {
  clarification: { cls: "bg-light text-dark border", label: "clarification" },
  interchangeable: { cls: "bg-info text-dark", label: "interchangeable" },
  breaking: { cls: "bg-danger", label: "breaking" },
};

const FIELD_LABELS = {
  product_id: "SKU",
  description: "Description",
  category: "Category",
  notes: "Notes",
  weight_lbs: "Weight (lbs)",
  min_order_qty: "Min order qty",
  order_in_qty_of: "Order in multiples of",
};

/**
 * Revision history for a purchased component, with two things the parts
 * revision card does not offer:
 *
 *   Diff     — what actually changed between two revisions, field by field.
 *              On parts the only answer is whatever prose someone typed.
 *   Restore  — re-apply an old revision's spec and record the fact as a new
 *              revision. parts has no rollback at all; the only escape from a
 *              bad part revision today is raw SQL.
 *
 * A shadow-twin shows the governing parts family's history instead, read-only.
 */
export default function ComponentRevisionTrail({ consumableId, productId, refreshKey, onChanged }) {
  const API_BASE = config.API_BASE_URL;

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [diff, setDiff] = useState(null);
  const [changes, setChanges] = useState([]);
  const [showChanges, setShowChanges] = useState(false);
  // Vendor acknowledgement (Slice 3). Optional by design — most revisions never
  // need one, so this stays collapsed until someone asks for it.
  const [ackFor, setAckFor] = useState(null);      // revision row being sent
  const [ackEmails, setAckEmails] = useState("");
  const [ackMessage, setAckMessage] = useState("");
  const [ackResult, setAckResult] = useState(null);

  const load = useCallback(async () => {
    if (!consumableId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/components/${consumableId}/revisions`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setPayload(await res.json());
    } catch (err) {
      setError(`Could not load revisions: ${err.message}`);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, consumableId]);

  const loadChanges = useCallback(async () => {
    if (!consumableId) return;
    try {
      const res = await fetch(`${API_BASE}/components/${consumableId}/field_changes?limit=50`);
      if (res.ok) setChanges(await res.json());
    } catch {
      /* the trail is still useful without the edit log */
    }
  }, [API_BASE, consumableId]);

  useEffect(() => {
    load();
    loadChanges();
    setDiff(null);
  }, [load, loadChanges, refreshKey]);

  const revisions = useMemo(() => payload?.revisions || [], [payload]);
  const readOnly = payload?.read_only === true;

  async function showDiff(newer, older) {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/components/${consumableId}/revisions/${older}/diff/${newer}`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Could not build the diff.");
      setDiff(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRestore(rev) {
    if (
      !window.confirm(
        `Restore the spec from Rev ${rev.revision_code}?\n\n` +
          `This re-applies that revision's values to ${productId} and records it as a NEW ` +
          `revision. Nothing is erased.`
      )
    )
      return;
    const pin = await pinPrompt(`PIN to restore Rev ${rev.revision_code}:`);
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/components/${consumableId}/revisions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, restore_from_revision_id: rev.component_revision_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Restore failed.");
      await load();
      await loadChanges();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function sendAckRequest() {
    const recipients = ackEmails
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!recipients.length) {
      setError("Enter at least one vendor email address.");
      return;
    }
    const pin = await pinPrompt(`PIN to send Rev ${ackFor.revision_code} to the vendor:`);
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const base = `${API_BASE}/components/${consumableId}/revisions/${ackFor.component_revision_id}`;
      // Generate the form first so the email carries a current one rather than
      // whatever was produced for an earlier send.
      const formRes = await fetch(`${base}/ack_form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const formBody = await formRes.json().catch(() => ({}));
      if (!formRes.ok) throw new Error(detailToString(formBody.detail) || "Could not build the form.");

      const res = await fetch(`${base}/ack_link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
        body: JSON.stringify({
          pin,
          send_email: true,
          recipient_emails: recipients,
          message: ackMessage.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(body.detail) || "Could not send the request.");
      setAckResult(body);
      setAckFor(null);
      setAckEmails("");
      setAckMessage("");
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function renderValue(v) {
    if (v === null || v === undefined || v === "") return <span className="text-muted">—</span>;
    return String(v);
  }

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>🕓 Revision history</strong>
        <div className="d-flex gap-2 align-items-center">
          {readOnly && <span className="badge bg-info text-dark">from the part</span>}
          {!readOnly && changes.length > 0 && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowChanges((v) => !v)}
            >
              {showChanges ? "Hide" : `Edits (${changes.length})`}
            </button>
          )}
        </div>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {loading && <div className="text-muted small">Loading…</div>}

        {readOnly && (
          <div className="alert alert-info py-2 small">
            {productId}'s revisions live on in-house part{" "}
            <a href={`/parts?part_id=${payload.governing_part_id}`}>{payload.governing_product_id}</a>.
          </div>
        )}

        {!loading && revisions.length === 0 && !readOnly && (
          <div className="text-muted small">No revisions yet.</div>
        )}

        {revisions.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Rev</th>
                  <th>What changed</th>
                  <th style={{ width: 150 }}>By</th>
                  <th style={{ width: 210 }}></th>
                </tr>
              </thead>
              <tbody>
                {revisions.map((r, idx) => {
                  if (readOnly) {
                    return (
                      <tr key={r.part_id}>
                        <td>
                          <span className={`badge ${r.is_current_revision ? "bg-dark" : "bg-secondary"}`}>
                            {r.revision_code}
                          </span>
                        </td>
                        <td className="text-muted small">
                          {r.is_current_revision ? "current" : "superseded"}
                        </td>
                        <td className="small text-muted">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                        </td>
                        <td />
                      </tr>
                    );
                  }
                  const impact = IMPACT_STYLE[r.stock_impact] || IMPACT_STYLE.clarification;
                  const older = revisions[idx + 1];
                  return (
                    <tr key={r.component_revision_id}>
                      <td>
                        <span className={`badge ${r.is_current ? "bg-dark" : "bg-secondary"}`}>
                          {r.revision_code}
                        </span>
                      </td>
                      <td>
                        <div>{r.change_description || <span className="text-muted">—</span>}</div>
                        <div className="small mt-1 d-flex gap-2 flex-wrap">
                          <span className={`badge ${impact.cls}`}>{impact.label}</span>
                          {r.change_requested_by && (
                            <span className="badge bg-light text-dark border">
                              asked by {r.change_requested_by}
                            </span>
                          )}
                          {r.pricing_impact === "revised" && (
                            <span className="badge bg-warning text-dark">
                              price revised{r.pricing_quote_ref ? ` · ${r.pricing_quote_ref}` : ""}
                            </span>
                          )}
                          {r.docs_added > 0 && (
                            <span className="badge bg-light text-dark border">
                              +{r.docs_added} file{r.docs_added !== 1 ? "s" : ""}
                            </span>
                          )}
                          {r.docs_retired > 0 && (
                            <span className="badge bg-light text-dark border">
                              −{r.docs_retired} retired
                            </span>
                          )}
                        </div>
                        {r.stock_disposition && (
                          <div className="small text-danger mt-1">
                            Shelf stock: {r.stock_disposition}
                          </div>
                        )}
                      </td>
                      <td className="small text-muted">
                        {r.created_by_name}
                        <br />
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                      </td>
                      <td className="text-end">
                        {older && (
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            disabled={busy}
                            onClick={() =>
                              showDiff(r.component_revision_id, older.component_revision_id)
                            }
                            title={`What changed between Rev ${older.revision_code} and Rev ${r.revision_code}`}
                          >
                            Diff
                          </button>
                        )}
                        {!r.is_current && (
                          <button
                            className="btn btn-sm btn-outline-secondary me-1"
                            disabled={busy}
                            onClick={() => handleRestore(r)}
                            title="Re-apply this revision's spec as a new revision"
                          >
                            Restore
                          </button>
                        )}
                        {r.is_current && !r.acked_via && (
                          <button
                            className="btn btn-sm btn-outline-dark"
                            disabled={busy}
                            onClick={() => { setAckFor(r); setAckResult(null); }}
                            title="Email the vendor a link to acknowledge this revision before they build to it"
                          >
                            Ask vendor
                          </button>
                        )}
                        {r.acked_via && (
                          <span className="badge bg-success" title={`Acknowledged by ${r.acked_by_name || "the vendor"}`}>
                            acknowledged
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {ackResult && (
          // The link always exists; `not_emailed` lists vendors SES refused (the message names them).
          <div className={`alert ${ackResult.not_emailed?.length ? "alert-warning" : "alert-success"} py-2 mt-3`}>
            <div>{ackResult.message}</div>
            <div className="small mt-1">
              Link (expires {ackResult.expires_at ? new Date(ackResult.expires_at).toLocaleDateString() : "—"}):{" "}
              <code>{ackResult.portal_url}</code>
            </div>
          </div>
        )}

        {ackFor && (
          <div className="border rounded p-3 mt-3">
            <div className="fw-semibold mb-1">
              Ask the vendor to acknowledge Rev {ackFor.revision_code}
            </div>
            <div className="form-text mb-2">
              They get a link showing what changed and the current drawings, and either
              acknowledge or ask for changes. Most revisions never need this — the drawing
              already rides along on the PO.
            </div>
            <div className="row g-2">
              <div className="col-md-6">
                <input
                  className="form-control form-control-sm"
                  value={ackEmails}
                  onChange={(e) => setAckEmails(e.target.value)}
                  placeholder="purchasing@boydmetals.com"
                />
              </div>
              <div className="col-md-6">
                <input
                  className="form-control form-control-sm"
                  value={ackMessage}
                  onChange={(e) => setAckMessage(e.target.value)}
                  placeholder="Optional note to include"
                />
              </div>
            </div>
            <div className="mt-2 d-flex gap-2">
              <button className="btn btn-sm btn-dark" disabled={busy} onClick={sendAckRequest}>
                {busy ? "Sending…" : "Send"}
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setAckFor(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {diff && (
          <div className="border rounded p-3 mt-3 bg-light">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>
                Rev {diff.from.revision_code} → Rev {diff.to.revision_code}
                <span className="text-muted ms-2 fw-normal">
                  {diff.changed_field_count} field{diff.changed_field_count !== 1 ? "s" : ""} changed
                </span>
              </strong>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setDiff(null)}>
                Close
              </button>
            </div>
            <table className="table table-sm mb-0 bg-white">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Field</th>
                  <th>Was</th>
                  <th>Now</th>
                </tr>
              </thead>
              <tbody>
                {diff.fields
                  .filter((f) => f.changed)
                  .map((f) => (
                    <tr key={f.name}>
                      <td className="fw-semibold">{FIELD_LABELS[f.name] || f.name}</td>
                      <td className="text-danger">{renderValue(f.old)}</td>
                      <td className="text-success">{renderValue(f.new)}</td>
                    </tr>
                  ))}
                {diff.documents_added.map((d) => (
                  <tr key={`a-${d.s3_key}`}>
                    <td className="fw-semibold">File added</td>
                    <td className="text-muted">—</td>
                    <td className="text-success">{d.display_name}</td>
                  </tr>
                ))}
                {diff.documents_removed.map((d) => (
                  <tr key={`r-${d.s3_key}`}>
                    <td className="fw-semibold">File retired</td>
                    <td className="text-danger">{d.display_name}</td>
                    <td className="text-muted">—</td>
                  </tr>
                ))}
                {diff.changed_field_count === 0 &&
                  diff.documents_added.length === 0 &&
                  diff.documents_removed.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        Nothing changed between these two — the revision was cut for the record.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        )}

        {showChanges && changes.length > 0 && (
          <div className="border rounded p-3 mt-3">
            <div className="fw-semibold mb-2">
              Edits made between revisions
              <span className="text-muted fw-normal ms-2 small">
                every in-place change to the spec, with a name against it
              </span>
            </div>
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Was</th>
                  <th>Now</th>
                  <th style={{ width: 170 }}>By</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.change_id}>
                    <td className="fw-semibold">{FIELD_LABELS[c.field_name] || c.field_name}</td>
                    <td className="text-danger small">{renderValue(c.old_value)}</td>
                    <td className="text-success small">{renderValue(c.new_value)}</td>
                    <td className="small text-muted">
                      {c.changed_by_name}
                      <br />
                      {c.changed_at ? new Date(c.changed_at).toLocaleString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
